import React, { useState } from 'react';
import useForm from '../../hooks/useForm';
import FormField from '../common/FormField';
import useAuth from '../../hooks/useAuth';
import './DecisionForm.css';

// Values mirror the backend exactly (DecisionRequest / config/tenantverify.php), not the display
// labels — see utils/applicationStatus.js for the same codes shown read-only elsewhere.
const OUTCOMES = [
  { value: 'shortlist', label: 'Shortlist' },
  { value: 'approve', label: 'Approve' },
  { value: 'decline', label: 'Decline' },
  { value: 'request_information', label: 'Request more information' }
];

const REASON_OPTIONS = [
  { value: 'AFF001', label: 'Affordability criteria not met' },
  { value: 'CRE002', label: 'Credit profile unacceptable' },
  { value: 'REF001', label: 'Unfavourable references received' },
  { value: 'INC001', label: 'Income unverifiable' },
  { value: 'ID001', label: 'Identity could not be verified' }
];

const ADVERSE = ['decline', 'request_information'];

const DecisionForm = ({ onSubmit, isSubmitting }) => {
  const { user } = useAuth();
  const [showWarning, setShowWarning] = useState(false);

  // The backend requires at least one reason code for every decision, not only adverse ones — a
  // record of "approved because X" matters as much as "declined because Y" for the audit trail.
  const validate = (vals) => {
    const errs = {};
    if (!vals.outcome) errs.outcome = 'Outcome is required.';
    if (vals.reasons.length === 0) errs.reasons = 'At least one reason is required, whatever the outcome.';
    if (vals.notes && vals.notes.length > 500) errs.notes = 'Keep this to 500 characters.';
    return errs;
  };

  const { values, errors, handleChange, handleSubmit, setFieldValue } = useForm({
    outcome: '',
    reasons: [],
    notes: '',
    conditions: ''
  }, validate);

  const onOutcomeChange = (e) => {
    handleChange(e);
    setShowWarning(ADVERSE.includes(e.target.value));
  };

  const handleReasonToggle = (val) => {
    const newReasons = values.reasons.includes(val)
      ? values.reasons.filter((r) => r !== val)
      : [...values.reasons, val];
    setFieldValue('reasons', newReasons);
  };

  const submit = (vals) => onSubmit({
    outcome: vals.outcome,
    reason_codes: vals.reasons,
    reason_notes: vals.notes || undefined,
    conditions: vals.outcome === 'approve' && vals.conditions ? vals.conditions : undefined
  });

  return (
    <form className="decision-form card" onSubmit={(e) => { e.preventDefault(); handleSubmit(submit); }}>
      <div className="card-header bg-primary text-white"><h2>Record Final Decision</h2></div>
      <div className="card-body">
        <FormField label="Decision Maker" name="decisionMaker" value={user?.first_name ? `${user.first_name} ${user.last_name}` : (user?.email || '')} disabled />

        <div className="form-group">
          <label className="form-label">Outcome</label>
          <div className="radio-group">
            {OUTCOMES.map(({ value, label }) => (
              <label key={value} className="radio-label">
                <input type="radio" name="outcome" value={value} checked={values.outcome === value} onChange={onOutcomeChange} />
                <span>{label}</span>
              </label>
            ))}
          </div>
          {errors.outcome && <p className="error-text">{errors.outcome}</p>}
        </div>

        {showWarning && (
          <div className="decision-warning">
            <strong>Notice:</strong> The applicant will be notified of this adverse decision and its material reasons. They retain the right to dispute.
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Reasons</label>
          <div className="checkbox-group">
            {REASON_OPTIONS.map((opt) => (
              <label key={opt.value} className="checkbox-label">
                <input type="checkbox" checked={values.reasons.includes(opt.value)} onChange={() => handleReasonToggle(opt.value)} />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
          {errors.reasons && <p className="error-text">{errors.reasons}</p>}
        </div>

        <FormField label="Notes (internal, max 500 characters)" name="notes" type="textarea" value={values.notes} onChange={handleChange} maxLength="500" error={errors.notes} />
        {values.outcome === 'approve' && <FormField label="Conditions (optional)" name="conditions" type="textarea" value={values.conditions} onChange={handleChange} />}

        <div className="decision-actions">
          <button type="submit" className="btn-primary" disabled={isSubmitting}>{isSubmitting ? 'Recording…' : 'Confirm Decision'}</button>
        </div>
      </div>
    </form>
  );
};
export default DecisionForm;
